import { Controller } from "@hotwired/stimulus"

// Handles the Participants tab functionality at the inloop level
// Based on design: present-mode-design/inloop/Participants.jpg
export default class extends Controller {
  static targets = [
    "emailInput",
    "inviteStatus",
    "participantsList",
    "selectAll",
    "participantCheckbox",
    "sendEmailModal",
    "emailLogsModal"
  ]

  static values = {
    inloopId: Number,
    activityId: Number,
    inviteUrl: String
  }

  connect() {
    // Initialize state
    this.selectedEmails = new Set()
    console.log("InloopParticipants controller connected", {
      hasSendEmailModal: this.hasSendEmailModalTarget,
      hasEmailLogsModal: this.hasEmailLogsModalTarget
    })
  }

  // Invite a single participant via email input
  async inviteParticipant(event) {
    event.preventDefault()

    const email = this.emailInputTarget.value.trim()
    if (!email) return

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      this.showStatus("Please enter a valid email address", "error")
      return
    }

    try {
      this.emailInputTarget.disabled = true
      this.showStatus("Sending invite...", "loading")

      const response = await fetch(this.inviteUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ emails: email })
      })

      const data = await response.json()

      if (response.ok && data.invited?.length > 0) {
        this.showStatus(`Invite sent to ${email}`, "success")
        this.emailInputTarget.value = ""

        // Refresh the page to show new participant
        // In future, we can use Turbo Streams for instant update
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        const errorMsg = data.errors?.[0] || data.error || "Failed to send invite"
        this.showStatus(errorMsg, "error")
      }
    } catch (error) {
      console.error("Invite error:", error)
      this.showStatus("Network error. Please try again.", "error")
    } finally {
      this.emailInputTarget.disabled = false
    }
  }

  // Resend invite to a participant
  async resendInvite(event) {
    const email = event.currentTarget.dataset.email
    if (!email) return

    try {
      const response = await fetch(this.inviteUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ emails: email })
      })

      if (response.ok) {
        this.showToast(`Invite resent to ${email}`)
      } else {
        this.showToast("Failed to resend invite", "error")
      }
    } catch (error) {
      console.error("Resend error:", error)
      this.showToast("Network error", "error")
    }
  }

  // Toggle select all checkboxes
  toggleSelectAll(event) {
    const checked = event.target.checked

    this.participantCheckboxTargets.forEach(checkbox => {
      checkbox.checked = checked
      const email = checkbox.dataset.email
      if (checked) {
        this.selectedEmails.add(email)
      } else {
        this.selectedEmails.delete(email)
      }
    })
  }

  // Handle individual checkbox change
  participantCheckboxTargetConnected(element) {
    element.addEventListener("change", (e) => {
      const email = element.dataset.email
      if (e.target.checked) {
        this.selectedEmails.add(email)
      } else {
        this.selectedEmails.delete(email)
        // Uncheck "select all" if any checkbox is unchecked
        if (this.hasSelectAllTarget) {
          this.selectAllTarget.checked = false
        }
      }
    })
  }

  // Open Send Email modal
  openSendEmailModal() {
    if (this.hasSendEmailModalTarget) {
      this.sendEmailModalTarget.classList.remove("hidden")

      // Wait a brief moment for the modal controller to initialize
      setTimeout(() => {
        // Get the send-email controller and populate selected emails
        const sendEmailController = this.sendEmailModalTarget.sendEmailController ||
          this.application.getControllerForElementAndIdentifier(
            this.sendEmailModalTarget,
            "send-email"
          )

        if (sendEmailController && this.selectedEmails.size > 0) {
          // Clear existing emails and add selected ones
          sendEmailController.additionalEmails = Array.from(this.selectedEmails)
          sendEmailController.renderTags()
        }
      }, 100)
    } else {
      console.error("Send email modal target not found")
    }
  }

  // Close Send Email modal
  closeSendEmailModal() {
    if (this.hasSendEmailModalTarget) {
      this.sendEmailModalTarget.classList.add("hidden")

      // Clear all checkboxes when modal is closed
      this.participantCheckboxTargets.forEach(checkbox => {
        checkbox.checked = false
      })
      this.selectedEmails.clear()

      // Uncheck select all checkbox
      if (this.hasSelectAllTarget) {
        this.selectAllTarget.checked = false
      }
    }
  }

  // Show Email Logs modal
  showEmailLogs() {
    if (this.hasEmailLogsModalTarget) {
      this.emailLogsModalTarget.classList.remove("hidden")
    } else {
      console.error("Email logs modal target not found")
    }
  }

  preserveParticipantsTab() {
    sessionStorage.setItem(`inloop-${this.inloopIdValue}-tab-return-once`, "participants")
  }

  // Close Email Logs modal
  closeEmailLogs() {
    if (this.hasEmailLogsModalTarget) {
      this.emailLogsModalTarget.classList.add("hidden")
    }
  }

  // Helper: Show status message below email input
  showStatus(message, type = "info") {
    if (!this.hasInviteStatusTarget) return

    this.inviteStatusTarget.textContent = message
    this.inviteStatusTarget.classList.remove("hidden", "text-green-600", "text-red-600", "text-gray-500")

    switch (type) {
      case "success":
        this.inviteStatusTarget.classList.add("text-green-600")
        break
      case "error":
        this.inviteStatusTarget.classList.add("text-red-600")
        break
      default:
        this.inviteStatusTarget.classList.add("text-gray-500")
    }

    // Auto-hide after 5 seconds for success messages
    if (type === "success") {
      setTimeout(() => {
        this.inviteStatusTarget.classList.add("hidden")
      }, 5000)
    }
  }

  // Helper: Show toast notification
  showToast(message, type = "success") {
    // Create toast element
    const toast = document.createElement("div")
    toast.className = `fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg text-white z-50 ${
      type === "error" ? "bg-red-600" : "bg-green-600"
    }`
    toast.textContent = message
    document.body.appendChild(toast)

    // Remove after 3 seconds
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }

  // Helper: Get CSRF token
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // Get selected emails for bulk operations
  get selectedParticipants() {
    return Array.from(this.selectedEmails)
  }
}
